import { z } from 'zod';

import { isHexColor } from '@/lib/color';

/**
 * An area's own colour - a header's, a section's, a block's - as the merchant
 * chose it. Its own module, with nothing of the storefront's imported, because
 * both the theme document and the section schema read it and the theme
 * document already imports the section schema.
 */

/**
 * The palette slots an area's colour may point at instead of carrying a hex.
 *
 * `accent` is the theme's accent token rather than one of the five colours a
 * merchant edits: the family's own warm note, or a built theme's override of
 * it, falling back to primary on a theme that has none (see `themeVariables`).
 */
export const PALETTE_COLOR_KEYS = ['primary', 'accent', 'background', 'surface', 'text', 'muted'] as const;

export type PaletteColorKey = (typeof PALETTE_COLOR_KEYS)[number];

export function isPaletteColorKey(value: unknown): value is PaletteColorKey {
  return (PALETTE_COLOR_KEYS as readonly unknown[]).includes(value);
}

/**
 * `null` for the theme's own, a palette slot - which follows the palette when
 * the merchant changes it - or a hex of their own. Anything else (a newer
 * build's notation, a hand-edited document) reads as the theme's own rather
 * than failing the whole theme.
 */
export const colorChoiceSchema = z
  .string()
  .nullish()
  .transform((value) => (isPaletteColorKey(value) || isHexColor(value) ? value.trim() : null))
  .optional();

export type ColorChoice = string | null | undefined;

/** A number of pixels a merchant may leave unset; unset is the theme's own. */
export const optionalPxSchema = z.number().nullish().optional();
